import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Checkbox } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { useGridApiContext } from '@mui/x-data-grid-pro';

import SelectionText from './SelectionText';
import { SelectionType } from './SelectorTypes';

import './MultiElementSelectorHeader.css';

export default function SelectionAndOrderController({
  selectedRowIds,
  resetSelection,
  selectionState,
  maxRowNum,
  selectMaxRows,
  totalRows,
  orderController,
}: {
  selectedRowIds?: GridRowSelectionModel;
  resetSelection?: () => void;
  selectionState: SelectionType;
  maxRowNum?: number;
  selectMaxRows?: () => void;
  totalRows: number;
  orderController: ReactElement;
}) {
  const apiRef = useGridApiContext();
  const isAllrowsSelected = useCallback(
    (totalRows: number, selectedRows: number) => {
      if (selectionState === 'selected') return selectedRows > 0;
      return selectedRows === totalRows && totalRows > 0;
    },
    [selectionState]
  );
  const [allSelected, setAllSelected] = useState<boolean>(
    isAllrowsSelected(totalRows, selectedRowIds ? selectedRowIds.ids.size : 0)
  );
  const [gridReady, setGridReady] = useState(false);

  const isIndeterminate = useCallback(
    (totalRows: number, selectedRows: number) => {
      if (selectionState === 'selected') return false;
      return selectedRows > 0 && selectedRows < totalRows;
    },
    [selectionState]
  );
  const [indeterminate, setIndeterminate] = useState<boolean>(
    isIndeterminate(totalRows, selectedRowIds ? selectedRowIds.ids.size : 0)
  );

  useEffect(() => {
    if (apiRef.current) {
      setGridReady(true);
    }
  }, []);

  useEffect(() => {
    if (!apiRef.current) return;

    const updateSelectionState = () => {
      const totalRows = apiRef.current.getRowsCount();
      const selectedRows = selectedRowIds ? selectedRowIds.ids.size : 0;

      setAllSelected(isAllrowsSelected(totalRows, selectedRows));
      setIndeterminate(isIndeterminate(totalRows, selectedRows));
    };

    return apiRef.current.subscribeEvent('rowSelectionChange', updateSelectionState);
  }, [apiRef, selectedRowIds, selectionState, isAllrowsSelected, isIndeterminate]);

  const toggleSelectAll = () => {
    if (!resetSelection || !selectMaxRows) return;
    if (allSelected || indeterminate) {
      resetSelection();
    } else {
      selectMaxRows();
    }
  };

  function getDisabledClass() {
    if (!selectedRowIds || !maxRowNum) return '';
    if (
      (selectionState === 'selected' && selectedRowIds.ids.size === 0) ||
      apiRef.current.getRowsCount() === 0 ||
      !gridReady
    ) {
      return 'disabled';
    }
    return '';
  }

  return (
    <div className="selector-toolbar" data-qa-element-id="target_selector_toolbar">
      <div className={`${getDisabledClass()}`}>
        <span>
          {selectionState !== 'single-select' && (
            <Checkbox
              data-qa-element-id="select_all_none_checkbox"
              className="selector-checkbox"
              indeterminate={indeterminate}
              checked={allSelected}
              onChange={toggleSelectAll}
            />
          )}
          <SelectionText
            maxElementcount={maxRowNum}
            rowCount={totalRows}
            selectedCount={selectedRowIds ? selectedRowIds.ids.size : undefined}
            selectionState={selectionState}
          />
        </span>
      </div>
      {orderController}
    </div>
  );
}
